import React, { useState, useCallback, useRef, useEffect } from "react";
import { useEditor, useValue } from "@tldraw/tldraw";

// Constants for snap
const SNAP_DISTANCE = 2;
const SNAP_DISTANCE_CENTER = 5; // Larger distance for center snap
const SLOW_SPEED = 10;

interface DragHandlerProps {
  canvasType: "front" | "back";
  position: { x: number; y: number } | null;
  size: number;
}

// Global map to track which canvas owns each shape
// This prevents one canvas from "stealing" shapes from another
const shapeOwnershipMap = new Map<string, "front" | "back">();

export function CanvasDragOverlay() {
  const editor = useEditor();

  // Get drag handler positions based on labels
  const MARGIN_FROM_LABEL = 0;
  const MIN_HANDLER_SIZE = 32;

  const frontData = useValue(
    "front-data",
    () => {
      const frontLabel = editor.getShape("shape:frontLabel" as any);
      if (!frontLabel) return null;

      // Get label dimensions
      const labelWidth = (frontLabel.props as any).w || 0;
      const labelHeight = (frontLabel.props as any).h || 0;

      // Convert label to screen coordinates
      const labelTopLeft = editor.pageToScreen({
        x: frontLabel.x,
        y: frontLabel.y,
      });

      const labelBottomRight = editor.pageToScreen({
        x: frontLabel.x + labelWidth,
        y: frontLabel.y + labelHeight,
      });

      // Calculate label height on screen (this will be the size of the handler)
      const labelScreenHeight = labelBottomRight.y - labelTopLeft.y;
      const handlerSize = Math.max(labelScreenHeight, MIN_HANDLER_SIZE);

      // Center vertically when the handler is larger than the label
      const yOffset = handlerSize > labelScreenHeight ? labelScreenHeight / 2 - handlerSize / 2 : 0;

      return {
        position: {
          x: labelBottomRight.x + MARGIN_FROM_LABEL,
          y: labelTopLeft.y + yOffset,
        },
        size: handlerSize,
        labelBounds: {
          x: labelTopLeft.x,
          y: labelTopLeft.y,
          width: labelBottomRight.x - labelTopLeft.x,
          height: labelBottomRight.y - labelTopLeft.y,
        },
      };
    },
    [editor]
  );

  const backData = useValue(
    "back-data",
    () => {
      const backLabel = editor.getShape("shape:backLabel" as any);
      if (!backLabel) return null;

      // Get label dimensions
      const labelWidth = (backLabel.props as any).w || 0;
      const labelHeight = (backLabel.props as any).h || 0;

      // Convert label to screen coordinates
      const labelTopLeft = editor.pageToScreen({
        x: backLabel.x,
        y: backLabel.y,
      });

      const labelBottomRight = editor.pageToScreen({
        x: backLabel.x + labelWidth,
        y: backLabel.y + labelHeight,
      });

      // Calculate label height on screen (this will be the size of the handler)
      const labelScreenHeight = labelBottomRight.y - labelTopLeft.y;
      const handlerSize = Math.max(labelScreenHeight, MIN_HANDLER_SIZE);

      // Center vertically when the handler is larger than the label
      const yOffset = handlerSize > labelScreenHeight ? labelScreenHeight / 2 - handlerSize / 2 : 0;

      return {
        position: {
          x: labelBottomRight.x + MARGIN_FROM_LABEL,
          y: labelTopLeft.y + yOffset,
        },
        size: handlerSize,
        labelBounds: {
          x: labelTopLeft.x,
          y: labelTopLeft.y,
          width: labelBottomRight.x - labelTopLeft.x,
          height: labelBottomRight.y - labelTopLeft.y,
        },
      };
    },
    [editor]
  );

  return (
    <>
      {frontData && <LabelClickOverlay bounds={frontData.labelBounds} />}
      {backData && <LabelClickOverlay bounds={backData.labelBounds} />}
      {frontData && <DragHandler canvasType="front" position={frontData.position} size={frontData.size} />}
      {backData && <DragHandler canvasType="back" position={backData.position} size={backData.size} />}
    </>
  );
}

function LabelClickOverlay({ bounds }: { bounds: { x: number; y: number; width: number; height: number } }) {
  const handleClick = useCallback(() => {
    window.dispatchEvent(new CustomEvent("openSettingsPanel"));
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        left: bounds.x,
        top: bounds.y,
        width: bounds.width,
        height: bounds.height,
        cursor: "pointer",
        zIndex: 5,
        pointerEvents: "auto",
      }}
      onClick={handleClick}
    />
  );
}

function DragHandler({ canvasType, position, size }: DragHandlerProps) {
  const editor = useEditor();
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const frameRef = useRef<number>();
  const shapeIdsToMoveRef = useRef<string[]>([]); // Only store IDs, not complete shapes

  // Refs for speed tracking (for snap)
  const speedRef = useRef(0);
  const previousPointRef = useRef({ x: 0, y: 0 });

  // State for snap lines and snap points visualization
  const [snapLines, setSnapLines] = useState<{ x1: number; y1: number; x2: number; y2: number }[]>([]);
  const [snapPoints, setSnapPoints] = useState<{ x: number; y: number }[]>([]);

  // Persistent set of shape IDs that are "adhered" to this canvas
  const adheredShapeIdsRef = useRef<Set<string>>(new Set());

  // Flag to track if this is the very first drag (initial load)
  const isInitializedRef = useRef(false);

  // Track shape positions BEFORE each drag starts
  const shapePositionsBeforeDragRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  // Track all shape positions to detect when shapes are moved manually
  const allShapePositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  // Helper to check if a shape can be adhered (not owned by another canvas)
  const canAdhereShape = useCallback(
    (shapeId: string): boolean => {
      const currentOwner = shapeOwnershipMap.get(shapeId);
      // Can adhere if: no owner, or already owned by this canvas
      return !currentOwner || currentOwner === canvasType;
    },
    [canvasType]
  );

  // Helper to adhere a shape to this canvas
  const adhereShape = useCallback(
    (shapeId: string) => {
      adheredShapeIdsRef.current.add(shapeId);
      shapeOwnershipMap.set(shapeId, canvasType);
    },
    [canvasType]
  );

  // Helper to release a shape from this canvas
  const releaseShape = useCallback(
    (shapeId: string) => {
      adheredShapeIdsRef.current.delete(shapeId);
      if (shapeOwnershipMap.get(shapeId) === canvasType) {
        shapeOwnershipMap.delete(shapeId);
      }
    },
    [canvasType]
  );

  // useEffect to initialize adhered shapes and listen for shape changes
  useEffect(() => {
    const prefix = canvasType === "front" ? "front" : "back";
    const otherPrefix = canvasType === "front" ? "back" : "front";
    const blackLineId = `shape:${prefix}BlackLine`;

    const canvasBaseShapeIds = [
      `shape:${prefix}BlackLine`,
      `shape:${prefix}RedLine`,
      `shape:${prefix}`,
      `shape:${prefix}BorderDashLine`,
      `shape:${prefix}Label`,
    ];

    // Initialize on mount
    if (!isInitializedRef.current) {
      const allShapes = editor.getCurrentPageShapes();
      const blackLine = allShapes.find((s) => s.id === blackLineId);

      if (blackLine) {
        const canvasBounds = {
          minX: blackLine.x,
          maxX: blackLine.x + (blackLine.props as any).w,
          minY: blackLine.y,
          maxY: blackLine.y + (blackLine.props as any).h,
        };

        isInitializedRef.current = true;

        // Initial scan: add all shapes that are currently overlapping
        allShapes.forEach((shape) => {
          if (canvasBaseShapeIds.includes(shape.id)) return;
          if (shape.id.includes(otherPrefix)) return;

          // Store initial position
          allShapePositionsRef.current.set(shape.id, { x: shape.x, y: shape.y });

          let shapeBounds;
          if (shape.type === "customDraw" || shape.type === "customHighlight") {
            try {
              const shapeUtil = editor.getShapeUtil(shape);
              const geometry = shapeUtil.getGeometry(shape);
              const bounds = geometry.bounds;
              shapeBounds = {
                x: shape.x + bounds.x,
                y: shape.y + bounds.y,
                width: bounds.w,
                height: bounds.h,
              };
            } catch (e) {
              return;
            }
          } else {
            shapeBounds = {
              x: shape.x,
              y: shape.y,
              width: (shape.props as any).w || 0,
              height: (shape.props as any).h || 0,
            };
          }

          const overlaps = !(
            shapeBounds.x + shapeBounds.width < canvasBounds.minX ||
            shapeBounds.x > canvasBounds.maxX ||
            shapeBounds.y + shapeBounds.height < canvasBounds.minY ||
            shapeBounds.y > canvasBounds.maxY
          );

          if (overlaps && canAdhereShape(shape.id)) {
            adhereShape(shape.id);
          }
        });
      }
    }

    // Helper function to check if a shape overlaps with canvas
    const checkShapeOverlap = (shape: any) => {
      if (canvasBaseShapeIds.includes(shape.id)) return false;
      if (shape.id.includes(otherPrefix)) return false;

      const blackLine = editor.getShape(blackLineId as any);
      if (!blackLine) return false;

      const canvasBounds = {
        minX: blackLine.x,
        maxX: blackLine.x + (blackLine.props as any).w,
        minY: blackLine.y,
        maxY: blackLine.y + (blackLine.props as any).h,
      };

      let shapeBounds;
      if (shape.type === "customDraw" || shape.type === "customHighlight") {
        try {
          const shapeUtil = editor.getShapeUtil(shape);
          const geometry = shapeUtil.getGeometry(shape);
          const bounds = geometry.bounds;
          shapeBounds = {
            x: shape.x + bounds.x,
            y: shape.y + bounds.y,
            width: bounds.w,
            height: bounds.h,
          };
        } catch (e) {
          return false;
        }
      } else {
        shapeBounds = {
          x: shape.x,
          y: shape.y,
          width: (shape.props as any).w || 0,
          height: (shape.props as any).h || 0,
        };
      }

      const overlaps = !(
        shapeBounds.x + shapeBounds.width < canvasBounds.minX ||
        shapeBounds.x > canvasBounds.maxX ||
        shapeBounds.y + shapeBounds.height < canvasBounds.minY ||
        shapeBounds.y > canvasBounds.maxY
      );

      return overlaps;
    };

    // Listen for newly created shapes
    const handleShapeCreated = (record: any) => {
      // Only process if not currently dragging the canvas
      if (isDraggingRef.current) return;

      const shape = record;
      if (!shape) return;

      // Store initial position
      allShapePositionsRef.current.set(shape.id, { x: shape.x, y: shape.y });

      // Check if the new shape overlaps with this canvas
      const overlaps = checkShapeOverlap(shape);

      if (overlaps && canAdhereShape(shape.id)) {
        // New shape created inside canvas → adhere it immediately
        adhereShape(shape.id);
      }
    };

    // Listen for shape changes to detect manual moves
    const handleShapeChange = (record: any) => {
      // Only process if not currently dragging the canvas
      if (isDraggingRef.current) return;

      const shape = record;
      if (!shape) return;

      if (canvasBaseShapeIds.includes(shape.id)) return;
      if (shape.id.includes(otherPrefix)) return;

      const previousPosition = allShapePositionsRef.current.get(shape.id);
      const currentPosition = { x: shape.x, y: shape.y };

      // Update position
      allShapePositionsRef.current.set(shape.id, currentPosition);

      // Check if shape moved
      if (previousPosition) {
        const shapeMoved =
          Math.abs(currentPosition.x - previousPosition.x) > 0.1 ||
          Math.abs(currentPosition.y - previousPosition.y) > 0.1;

        if (shapeMoved) {
          // Shape moved, check if it overlaps with canvas
          const overlaps = checkShapeOverlap(shape);

          if (overlaps && canAdhereShape(shape.id)) {
            // Shape moved and now overlaps → adhere it
            adhereShape(shape.id);
          } else if (!overlaps) {
            // Shape moved and no longer overlaps → remove it
            releaseShape(shape.id);
          }
        }
      }
    };

    // Listen to shape changes and creations
    const unsubscribeChange = editor.store.listen(
      ({ changes }) => {
        Object.values(changes.updated).forEach((record) => {
          if (record[1].typeName === "shape") {
            handleShapeChange(record[1]);
          }
        });
      },
      { scope: "document", source: "user" }
    );

    const unsubscribeCreate = editor.store.listen(
      ({ changes }) => {
        Object.values(changes.added).forEach((record) => {
          if (record.typeName === "shape") {
            handleShapeCreated(record);
          }
        });
      },
      { scope: "document" }
    );

    return () => {
      unsubscribeChange();
      unsubscribeCreate();
    };
  }, [editor, canvasType, canAdhereShape, adhereShape, releaseShape]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();

      // Calculate which shapes to move at the start of the drag
      const allShapes = editor.getCurrentPageShapes();
      const prefix = canvasType === "front" ? "front" : "back";
      const otherPrefix = canvasType === "front" ? "back" : "front";

      const blackLineId = `shape:${prefix}BlackLine`;
      const blackLine = allShapes.find((s) => s.id === blackLineId);

      if (blackLine) {
        // Exact IDs of the base shapes of the canvas
        const canvasBaseShapeIds = [
          `shape:${prefix}BlackLine`,
          `shape:${prefix}RedLine`,
          `shape:${prefix}`,
          `shape:${prefix}BorderDashLine`,
          `shape:${prefix}Label`,
        ];

        // Store positions of ALL shapes before this drag
        shapePositionsBeforeDragRef.current.clear();
        allShapes.forEach((shape) => {
          if (!canvasBaseShapeIds.includes(shape.id) && !shape.id.includes(otherPrefix)) {
            shapePositionsBeforeDragRef.current.set(shape.id, { x: shape.x, y: shape.y });
          }
        });

        // Build the list of shapes to move from the adhered set
        const shapesToMove = allShapes.filter((shape) => {
          if (canvasBaseShapeIds.includes(shape.id)) return true;
          if (shape.id.includes(otherPrefix)) return false;
          return adheredShapeIdsRef.current.has(shape.id);
        });

        // Only save IDs, not the complete shapes
        shapeIdsToMoveRef.current = shapesToMove.map((s) => s.id);
      }

      setIsDragging(true);
      isDraggingRef.current = true;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      previousPointRef.current = { x: e.clientX, y: e.clientY }; // Initialize previous point
      speedRef.current = 0; // Reset speed
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [editor, canvasType]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingRef.current) return;

      // Safety check: if no button is pressed, stop dragging
      if (e.buttons === 0) {
        setIsDragging(false);
        isDraggingRef.current = false;
        shapeIdsToMoveRef.current = [];
        setSnapLines([]);
        setSnapPoints([]);
        if (frameRef.current) {
          cancelAnimationFrame(frameRef.current);
        }
        return;
      }

      e.stopPropagation();
      e.preventDefault();

      // Cancel previous frame if it exists
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      // Schedule update in the next frame
      frameRef.current = requestAnimationFrame(() => {
        if (shapeIdsToMoveRef.current.length === 0) {
          return;
        }

        const camera = editor.getCamera();
        const zoom = camera.z;

        // Calculate speed to determine if snap should be applied
        const currentPoint = { x: e.clientX, y: e.clientY };
        const previousPoint = previousPointRef.current;

        const speed = Math.sqrt(
          Math.pow(currentPoint.x - previousPoint.x, 2) + Math.pow(currentPoint.y - previousPoint.y, 2)
        );

        const change = speed - speedRef.current;
        speedRef.current = speedRef.current + change * (change > 1 ? 0.5 : 0.15);
        previousPointRef.current = currentPoint;

        let deltaX = (e.clientX - dragStartRef.current.x) / zoom;
        let deltaY = (e.clientY - dragStartRef.current.y) / zoom;

        // SNAP LOGIC - Only apply snap if the speed is low
        if (speedRef.current * zoom < SLOW_SPEED) {
          const prefix = canvasType === "front" ? "front" : "back";
          const otherPrefix = canvasType === "front" ? "back" : "front";

          // Get the blackLine of the canvas that is being moved
          const movingBlackLine = editor.getShape(`shape:${prefix}BlackLine` as any);

          // Get the blackLine of the other canvas (for snap)
          const otherBlackLine = editor.getShape(`shape:${otherPrefix}BlackLine` as any);

          if (movingBlackLine && otherBlackLine) {
            // Calculate projected bounds of the canvas that is being moved
            const movingBounds = {
              minX: movingBlackLine.x + deltaX,
              maxX: movingBlackLine.x + (movingBlackLine.props as any).w + deltaX,
              minY: movingBlackLine.y + deltaY,
              maxY: movingBlackLine.y + (movingBlackLine.props as any).h + deltaY,
              width: (movingBlackLine.props as any).w,
              height: (movingBlackLine.props as any).h,
            };

            // Bounds of the other canvas (target for snap)
            const targetBounds = {
              minX: otherBlackLine.x,
              maxX: otherBlackLine.x + (otherBlackLine.props as any).w,
              minY: otherBlackLine.y,
              maxY: otherBlackLine.y + (otherBlackLine.props as any).h,
              width: (otherBlackLine.props as any).w,
              height: (otherBlackLine.props as any).h,
            };

            const EPS = SNAP_DISTANCE / zoom;

            // Check snap on the X and Y axes
            let snapOffsetX = 0;
            let snapOffsetY = 0;
            const newSnapLines: { x1: number; y1: number; x2: number; y2: number }[] = [];
            const newSnapPoints: { x: number; y: number }[] = [];

            // Calculate centers for both axes
            const movingCenterX = movingBounds.minX + movingBounds.width / 2;
            const targetCenterX = targetBounds.minX + targetBounds.width / 2;
            const movingCenterY = movingBounds.minY + movingBounds.height / 2;
            const targetCenterY = targetBounds.minY + targetBounds.height / 2;

            // Snap horizontal (left, center, right)
            const snapDistances = [
              {
                edge: "left-left",
                dist: Math.abs(movingBounds.minX - targetBounds.minX),
                offset: targetBounds.minX - movingBounds.minX,
              },
              {
                edge: "left-right",
                dist: Math.abs(movingBounds.minX - targetBounds.maxX),
                offset: targetBounds.maxX - movingBounds.minX,
              },
              {
                edge: "left-center",
                dist: Math.abs(movingBounds.minX - targetCenterX),
                offset: targetCenterX - movingBounds.minX,
              },
              {
                edge: "right-left",
                dist: Math.abs(movingBounds.maxX - targetBounds.minX),
                offset: targetBounds.minX - movingBounds.maxX,
              },
              {
                edge: "right-right",
                dist: Math.abs(movingBounds.maxX - targetBounds.maxX),
                offset: targetBounds.maxX - movingBounds.maxX,
              },
              {
                edge: "right-center",
                dist: Math.abs(movingBounds.maxX - targetCenterX),
                offset: targetCenterX - movingBounds.maxX,
              },
              {
                edge: "center-left",
                dist: Math.abs(movingCenterX - targetBounds.minX),
                offset: targetBounds.minX - movingCenterX,
              },
              {
                edge: "center-right",
                dist: Math.abs(movingCenterX - targetBounds.maxX),
                offset: targetBounds.maxX - movingCenterX,
              },
              {
                edge: "center-center",
                dist: Math.abs(movingCenterX - targetCenterX),
                offset: targetCenterX - movingCenterX,
              },
            ];

            // Find ALL snaps that are within the distance
            const validHorizontalSnaps = snapDistances.filter((snap) => {
              const snapEPS = snap.edge.includes("center") ? SNAP_DISTANCE_CENTER / zoom : EPS;
              return snap.dist < snapEPS;
            });

            // If there are valid snaps, use the closest one for the offset
            if (validHorizontalSnaps.length > 0) {
              const closestHorizontalSnap = validHorizontalSnaps.reduce((min, snap) =>
                snap.dist < min.dist ? snap : min
              );
              snapOffsetX = closestHorizontalSnap.offset;

              // Draw lines for ALL valid snaps
              validHorizontalSnaps.forEach((currentSnap) => {
                // Determine X position of the snap line
                let snapX = 0;
                switch (currentSnap.edge) {
                  case "left-left":
                    snapX = targetBounds.minX;
                    break;
                  case "left-right":
                    snapX = targetBounds.maxX;
                    break;
                  case "left-center":
                    snapX = targetCenterX;
                    break;
                  case "right-left":
                    snapX = targetBounds.minX;
                    break;
                  case "right-right":
                    snapX = targetBounds.maxX;
                    break;
                  case "right-center":
                    snapX = targetCenterX;
                    break;
                  case "center-left":
                    snapX = targetBounds.minX;
                    break;
                  case "center-right":
                    snapX = targetBounds.maxX;
                    break;
                  case "center-center":
                    snapX = targetCenterX;
                    break;
                }

                // Create vertical snap line
                let lineY1, lineY2;

                // If there is no center involved, use the entire extension of both canvases
                if (!currentSnap.edge.includes("center")) {
                  lineY1 = Math.min(movingBounds.minY, targetBounds.minY);
                  lineY2 = Math.max(movingBounds.maxY, targetBounds.maxY);
                } else if (currentSnap.edge === "center-center") {
                  // If it's center-center, only from center to center
                  lineY1 = movingCenterY;
                  lineY2 = targetCenterY;
                } else {
                  // If there is a side and a center, the line must go from the center and cover the entire side
                  if (currentSnap.edge.startsWith("center")) {
                    // center-left or center-right: from the center of the moving canvas, cover the entire side of the target
                    lineY1 = Math.min(movingCenterY, targetBounds.minY);
                    lineY2 = Math.max(movingCenterY, targetBounds.maxY);
                  } else {
                    // left-center or right-center: from the entire side of the moving canvas, reach the center of the target
                    lineY1 = Math.min(movingBounds.minY, targetCenterY);
                    lineY2 = Math.max(movingBounds.maxY, targetCenterY);
                  }
                }

                newSnapLines.push({ x1: snapX, y1: lineY1, x2: snapX, y2: lineY2 });

                // Add snap points (crosshairs) at reference points
                // Add crosshairs according to the type of snap
                if (currentSnap.edge.startsWith("center")) {
                  // Crosshair in the center of the moving canvas
                  newSnapPoints.push({ x: snapX, y: movingCenterY });
                } else {
                  // Crosshairs at the corners of the moving canvas (top and bottom)
                  newSnapPoints.push({ x: snapX, y: movingBounds.minY });
                  newSnapPoints.push({ x: snapX, y: movingBounds.maxY });
                }

                if (currentSnap.edge.endsWith("center")) {
                  // Crosshair in the center of the target canvas
                  newSnapPoints.push({ x: snapX, y: targetCenterY });
                } else {
                  // Crosshairs at the corners of the target canvas (top and bottom)
                  newSnapPoints.push({ x: snapX, y: targetBounds.minY });
                  newSnapPoints.push({ x: snapX, y: targetBounds.maxY });
                }
              });
            }

            // Snap vertical (top, center, bottom)
            const verticalSnapDistances = [
              {
                edge: "top-top",
                dist: Math.abs(movingBounds.minY - targetBounds.minY),
                offset: targetBounds.minY - movingBounds.minY,
              },
              {
                edge: "top-bottom",
                dist: Math.abs(movingBounds.minY - targetBounds.maxY),
                offset: targetBounds.maxY - movingBounds.minY,
              },
              {
                edge: "top-center",
                dist: Math.abs(movingBounds.minY - targetCenterY),
                offset: targetCenterY - movingBounds.minY,
              },
              {
                edge: "bottom-top",
                dist: Math.abs(movingBounds.maxY - targetBounds.minY),
                offset: targetBounds.minY - movingBounds.maxY,
              },
              {
                edge: "bottom-bottom",
                dist: Math.abs(movingBounds.maxY - targetBounds.maxY),
                offset: targetBounds.maxY - movingBounds.maxY,
              },
              {
                edge: "bottom-center",
                dist: Math.abs(movingBounds.maxY - targetCenterY),
                offset: targetCenterY - movingBounds.maxY,
              },
              {
                edge: "center-top",
                dist: Math.abs(movingCenterY - targetBounds.minY),
                offset: targetBounds.minY - movingCenterY,
              },
              {
                edge: "center-bottom",
                dist: Math.abs(movingCenterY - targetBounds.maxY),
                offset: targetBounds.maxY - movingCenterY,
              },
              {
                edge: "center-center",
                dist: Math.abs(movingCenterY - targetCenterY),
                offset: targetCenterY - movingCenterY,
              },
            ];

            // Find ALL vertical snaps that are within the distance
            const validVerticalSnaps = verticalSnapDistances.filter((snap) => {
              const snapEPS = snap.edge.includes("center") ? SNAP_DISTANCE_CENTER / zoom : EPS;
              return snap.dist < snapEPS;
            });

            // If there are valid snaps, use the closest one for the offset
            if (validVerticalSnaps.length > 0) {
              const closestVerticalSnap = validVerticalSnaps.reduce((min, snap) => (snap.dist < min.dist ? snap : min));
              snapOffsetY = closestVerticalSnap.offset;

              // Draw lines for ALL valid snaps
              validVerticalSnaps.forEach((currentSnap) => {
                // Determine Y position of the snap line
                let snapY = 0;
                switch (currentSnap.edge) {
                  case "top-top":
                    snapY = targetBounds.minY;
                    break;
                  case "top-bottom":
                    snapY = targetBounds.maxY;
                    break;
                  case "top-center":
                    snapY = targetCenterY;
                    break;
                  case "bottom-top":
                    snapY = targetBounds.minY;
                    break;
                  case "bottom-bottom":
                    snapY = targetBounds.maxY;
                    break;
                  case "bottom-center":
                    snapY = targetCenterY;
                    break;
                  case "center-top":
                    snapY = targetBounds.minY;
                    break;
                  case "center-bottom":
                    snapY = targetBounds.maxY;
                    break;
                  case "center-center":
                    snapY = targetCenterY;
                    break;
                }

                // Create horizontal snap line
                let lineX1, lineX2;

                // If there is no center involved, use the entire extension of both canvases
                if (!currentSnap.edge.includes("center")) {
                  lineX1 = Math.min(movingBounds.minX, targetBounds.minX);
                  lineX2 = Math.max(movingBounds.maxX, targetBounds.maxX);
                } else if (currentSnap.edge === "center-center") {
                  // If it's center-center, only from center to center
                  lineX1 = movingCenterX;
                  lineX2 = targetCenterX;
                } else {
                  // If there is a side and a center, the line must go from the center and cover the entire side
                  if (currentSnap.edge.startsWith("center")) {
                    // center-top or center-bottom: from the center of the moving canvas, cover the entire side of the target
                    lineX1 = Math.min(movingCenterX, targetBounds.minX);
                    lineX2 = Math.max(movingCenterX, targetBounds.maxX);
                  } else {
                    // top-center or bottom-center: from the entire side of the moving canvas, reach the center of the target
                    lineX1 = Math.min(movingBounds.minX, targetCenterX);
                    lineX2 = Math.max(movingBounds.maxX, targetCenterX);
                  }
                }

                newSnapLines.push({ x1: lineX1, y1: snapY, x2: lineX2, y2: snapY });

                // Add snap points (crosshairs) at reference points
                // Add crosshairs according to the type of snap
                if (currentSnap.edge.startsWith("center")) {
                  // Crosshair in the center of the moving canvas
                  newSnapPoints.push({ x: movingCenterX, y: snapY });
                } else {
                  // Crosshairs at the corners of the moving canvas (left and right)
                  newSnapPoints.push({ x: movingBounds.minX, y: snapY });
                  newSnapPoints.push({ x: movingBounds.maxX, y: snapY });
                }

                if (currentSnap.edge.endsWith("center")) {
                  // Crosshair in the center of the target canvas
                  newSnapPoints.push({ x: targetCenterX, y: snapY });
                } else {
                  // Crosshairs at the corners of the target canvas (left and right)
                  newSnapPoints.push({ x: targetBounds.minX, y: snapY });
                  newSnapPoints.push({ x: targetBounds.maxX, y: snapY });
                }
              });
            }

            // Apply snap offset
            deltaX += snapOffsetX;
            deltaY += snapOffsetY;

            // Update snap lines and snap points visualization
            setSnapLines(newSnapLines);
            setSnapPoints(newSnapPoints);
          } else {
            setSnapLines([]);
            setSnapPoints([]);
          }
        } else {
          // If the speed is high, clear snap lines
          setSnapLines([]);
        }

        // Convert delta of pixels to canvas units (already calculated above with snap)

        // Get current shapes from editor (fresh positions)
        const currentShapes = shapeIdsToMoveRef.current.map((id) => editor.getShape(id as any)).filter(Boolean);

        if (currentShapes.length === 0) {
          return;
        }

        // Temporarily unlock shapes of the canvas to be able to move them
        const lockedShapes = currentShapes.filter((s) => s!.isLocked);

        editor.batch(() => {
          // 1. Unlock shapes that are locked
          if (lockedShapes.length > 0) {
            editor.updateShapes(
              lockedShapes.map((s) => ({
                id: s!.id,
                type: s!.type,
                isLocked: false,
              }))
            );
          }

          // 2. Move all shapes (using deltaX and deltaY with snap applied)
          editor.updateShapes(
            currentShapes.map((s) => {
              const update: any = {
                id: s!.id,
                type: s!.type,
                x: s!.x + deltaX,
                y: s!.y + deltaY,
              };

              // Labels need allowUpdate: true to be able to change their position
              if (s!.type === "customLabel") {
                update.props = { allowUpdate: true };
              }

              return update;
            })
          );

          // 3. Lock shapes that were locked again
          if (lockedShapes.length > 0) {
            editor.updateShapes(
              lockedShapes.map((s) => ({
                id: s!.id,
                type: s!.type,
                isLocked: true,
              }))
            );
          }
        });

        // Update drag start for the next movement
        dragStartRef.current = { x: e.clientX, y: e.clientY };
      });
    },
    [isDragging, editor, canvasType]
  );

  const cleanup = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    shapeIdsToMoveRef.current = [];
    setSnapLines([]);
    setSnapPoints([]);
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = undefined;
    }
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (isDragging) {
        const allShapes = editor.getCurrentPageShapes();
        const prefix = canvasType === "front" ? "front" : "back";
        const otherPrefix = canvasType === "front" ? "back" : "front";
        const blackLineId = `shape:${prefix}BlackLine`;
        const blackLine = allShapes.find((s) => s.id === blackLineId);

        if (blackLine) {
          const canvasBounds = {
            minX: blackLine.x,
            maxX: blackLine.x + (blackLine.props as any).w,
            minY: blackLine.y,
            maxY: blackLine.y + (blackLine.props as any).h,
          };

          allShapes.forEach((shape) => {
            // Skip base shapes and other canvas shapes
            if (
              shape.id.includes("BlackLine") ||
              shape.id.includes("RedLine") ||
              shape.id.includes("BorderDashLine") ||
              shape.id.includes("Label") ||
              shape.id === "shape:front" ||
              shape.id === "shape:back" ||
              shape.id.includes(otherPrefix)
            ) {
              return;
            }

            const wasMovedByCanvas = shapeIdsToMoveRef.current.includes(shape.id);

            if (!wasMovedByCanvas) {
              // Check if the shape ACTUALLY MOVED (not just that canvas moved over it)
              const previousPosition = shapePositionsBeforeDragRef.current.get(shape.id);

              if (previousPosition) {
                const shapeMoved =
                  Math.abs(shape.x - previousPosition.x) > 0.1 || Math.abs(shape.y - previousPosition.y) > 0.1;

                // Only add to adhered set if the shape itself moved AND now overlaps
                if (shapeMoved) {
                  let shapeBounds;
                  if (shape.type === "customDraw" || shape.type === "customHighlight") {
                    try {
                      const shapeUtil = editor.getShapeUtil(shape);
                      const geometry = shapeUtil.getGeometry(shape);
                      const bounds = geometry.bounds;
                      shapeBounds = {
                        x: shape.x + bounds.x,
                        y: shape.y + bounds.y,
                        width: bounds.w,
                        height: bounds.h,
                      };
                    } catch (e) {
                      return;
                    }
                  } else {
                    shapeBounds = {
                      x: shape.x,
                      y: shape.y,
                      width: (shape.props as any).w || 0,
                      height: (shape.props as any).h || 0,
                    };
                  }

                  const overlaps = !(
                    shapeBounds.x + shapeBounds.width < canvasBounds.minX ||
                    shapeBounds.x > canvasBounds.maxX ||
                    shapeBounds.y + shapeBounds.height < canvasBounds.minY ||
                    shapeBounds.y > canvasBounds.maxY
                  );

                  if (overlaps && canAdhereShape(shape.id)) {
                    // Shape was moved by user (not canvas) AND now overlaps → adhere
                    adhereShape(shape.id);
                  } else if (!overlaps) {
                    // Shape was moved by user but is now outside → remove if was adhered
                    releaseShape(shape.id);
                  }
                }
                // If shape didn't move, don't add it to adhered set
              }
            } else {
              // Shape was moved by canvas, check if it still overlaps
              let shapeBounds;
              if (shape.type === "customDraw" || shape.type === "customHighlight") {
                try {
                  const shapeUtil = editor.getShapeUtil(shape);
                  const geometry = shapeUtil.getGeometry(shape);
                  const bounds = geometry.bounds;
                  shapeBounds = {
                    x: shape.x + bounds.x,
                    y: shape.y + bounds.y,
                    width: bounds.w,
                    height: bounds.h,
                  };
                } catch (e) {
                  return;
                }
              } else {
                shapeBounds = {
                  x: shape.x,
                  y: shape.y,
                  width: (shape.props as any).w || 0,
                  height: (shape.props as any).h || 0,
                };
              }

              const overlaps = !(
                shapeBounds.x + shapeBounds.width < canvasBounds.minX ||
                shapeBounds.x > canvasBounds.maxX ||
                shapeBounds.y + shapeBounds.height < canvasBounds.minY ||
                shapeBounds.y > canvasBounds.maxY
              );

              if (!overlaps) {
                // Shape was moved out of canvas → remove from adhered set
                releaseShape(shape.id);
              }
            }
          });
        }
      }

      cleanup();
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch (error) {
        // Ignore errors if pointer capture was already released
      }
    },
    [isDragging, cleanup, editor, canvasType, canAdhereShape, adhereShape, releaseShape]
  );

  const handleLostPointerCapture = useCallback(() => {
    // Called when pointer capture is lost (e.g., user switches windows, etc.)
    cleanup();
  }, [cleanup]);

  if (!position) return null;

  return (
    <>
      {/* Snap lines overlay - Fixed position with screen coordinates */}
      {isDragging && snapLines.length > 0 && (
        <svg
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            pointerEvents: "none",
            zIndex: 999999,
            overflow: "visible",
          }}
        >
          {snapLines.map((line, i) => {
            // Convert canvas coordinates to screen coordinates using pageToScreen
            const screenStart = editor.pageToScreen({ x: line.x1, y: line.y1 });
            const screenEnd = editor.pageToScreen({ x: line.x2, y: line.y2 });

            return (
              <line
                key={i}
                x1={screenStart.x}
                y1={screenStart.y}
                x2={screenEnd.x}
                y2={screenEnd.y}
                stroke="#FF0000"
                strokeWidth={1}
                opacity={1}
              />
            );
          })}

          {/* Draw crosshairs at 45° in the snap points */}
          {snapPoints.map((point, i) => {
            const screenPoint = editor.pageToScreen({ x: point.x, y: point.y });
            const size = 4; // Size of the crosshair

            return (
              <g key={`point-${i}`}>
                {/* Diagonal line \ (top-left to bottom-right) */}
                <line
                  x1={screenPoint.x - size}
                  y1={screenPoint.y - size}
                  x2={screenPoint.x + size}
                  y2={screenPoint.y + size}
                  stroke="#1d9bf0"
                  strokeWidth={1.5}
                  opacity={1}
                />
                {/* Diagonal line / (top-right to bottom-left) */}
                <line
                  x1={screenPoint.x + size}
                  y1={screenPoint.y - size}
                  x2={screenPoint.x - size}
                  y2={screenPoint.y + size}
                  stroke="#1d9bf0"
                  strokeWidth={1.5}
                  opacity={1}
                />
              </g>
            );
          })}
        </svg>
      )}

      {/* Drag handler button */}
      <div
        style={{
          position: "fixed",
          left: position.x,
          top: position.y,
          width: size,
          height: size,
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          backgroundColor: isDragging ? "#FAFAFA" : "",
          boxShadow: isDragging ? "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)" : "",
          justifyContent: "center",
          cursor: isDragging ? "grabbing" : "grab",
          color: "#000000",
          fontSize: size * 0.5,
          fontWeight: "bold",
          userSelect: "none",
          zIndex: 5,
          transition: "transform 0.15s ease",
          transform: isDragging ? "scale(1.05)" : "scale(1)",
          touchAction: "none",
          pointerEvents: "auto",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onLostPointerCapture={handleLostPointerCapture}
        title={`Drag to move ${canvasType} canvas`}
      >
        ⋮⋮
      </div>
    </>
  );
}
